<!-- this dialog provides a user-friendly interface for selecting or creating positions within an organization, with options to expand/collapse the tree view and handle different selection modes. -->

<h2 mat-dialog-title>Select Org Part

  <!-- Close button. -->
  <button mat-icon-button mat-dialog-close class="close-button">
    <mat-icon>close</mat-icon>
  </button>
</h2>

<mat-dialog-content>

  <div class="view-container m-3 w-100">

    <div class="view-header">
      <span class="viewer-title-spacer">
        <!-- Select or create new position -->

        <shared-searchable-select 
          class="flex-1-auto"
          [displayValue]="false"
          [appearance]="'outline'"
          [classes]="['w-75']"
          [label]="'Search or create new org part'"
          [data]="searchablePositions" 
          [control]="positionControl"
          (selected)="onSelectInitialPart($event)"
          [multiple]="false"
        ></shared-searchable-select>
        <button mat-stroked-button class="ml-3" color="accent" [disabled]="!positionControl.value" (click)="onClear()">Clear<mat-icon>clear</mat-icon></button>
      </span>

      <!-- Expand all and collapse all buttons. -->
      <div class="expand-collapse-buttons">
        <button mat-raised-button class="mb-3" color="primary" (click)="expandAll()">Expand All
          <mat-icon>expand_more</mat-icon></button>
        <button mat-raised-button class="mb-3" color="accent" (click)="collapseAll()">Collapse All
          <mat-icon>chevron_right</mat-icon></button>
      </div>
    </div>

    <div class="view-content">

      <div class="mh-5px">
        @if(showLoader) {
          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
        }
      </div>

      <mat-tree [dataSource]="dataSource" [treeControl]="treeControlSecurityGroups">

        <!-- Radio button selection -->
        @if (data.config.selection === 'single') {
        <mat-radio-group [(ngModel)]="selectedPosition" (ngModelChange)="onSelectPosition()">

          <mat-tree-node *matTreeNodeDef="let node" matTreeNodePadding
            [ngStyle]="node.selected ? {'opacity': '100%' } : {'opacity': '30%' }">
            <!-- use a disabled button to provide padding -->
            <button mat-icon-button disabled></button>

            <div class="node-actions">
              
              <!-- If the node has a key, it's a valid position. -->
              @if (node.key) {
                <mat-radio-button [value]="node" color="primary" [labelPosition]="'after'">{{node.name}}</mat-radio-button>
                @if (data.config.allowCreate) {
                <button mat-icon-button color="accent" [matMenuTriggerFor]="menu"><mat-icon>add</mat-icon></button>
                }   

              <!-- Else if a node doesn't have a key, it's a mock node to indicate the user must create a position to proceed. -->
              } @else {
                <!-- Show message and add button. -->
                @if (data.config.allowCreate) {
                  <span class="t-10">{{node.name}}</span>
                  <button mat-icon-button color="accent" (click)="createNewPosition(node, 'sibling');"><mat-icon>add</mat-icon></button>
                
                <!-- Show message that they must contact admin. -->
                } @else {
                  <span class="t-10">You aren't allowed to create org parts, please contact your administrator.</span>
                }
              }

              <!-- Spinner while things are loading. -->
              @if(node.showLoader) { <mat-spinner class="ml-3" diameter="40" color="accent"></mat-spinner> }
            </div>

            <mat-menu #menu="matMenu">
              <button mat-menu-item color="primary" (click)="createNewPosition(node, 'sibling');">
                <mat-icon color="primary">add</mat-icon>
                <span>Sibling</span>
              </button>
              <button mat-menu-item color="accent" (click)="createNewPosition(node, 'child');">
                <mat-icon color="accent">add</mat-icon>
                <span>Child</span>
              </button>
            </mat-menu>

          </mat-tree-node>

          <!-- This is the tree node template for expandable nodes -->
          <mat-tree-node *matTreeNodeDef="let node;when: hasChild" matTreeNodePadding
            [ngStyle]="node.selected ? {'opacity': '100%' } : {'opacity': '30%' }">

            <button mat-icon-button matTreeNodeToggle 
              style="align-self: flex-start;"
              [id]="'expand-btn-' + node.expandedKey"
              [attr.aria-label]="'Toggle ' + node.name"
              (click)="onToggleNodeExpand(node, treeControlSecurityGroups.isExpanded(node))">
              <mat-icon class="mat-icon-rtl-mirror">{{treeControlSecurityGroups.isExpanded(node) ? 'expand_more' :
                'chevron_right'}}</mat-icon>
            </button>
            
            <div class="node-actions">

              <!-- If the node has a key, it's a valid position. -->
              @if (node.key) {
                <mat-radio-button [value]="node" color="primary" [labelPosition]="'after'">{{node.name}}</mat-radio-button>
                @if (data.config.allowCreate) {
                <button mat-icon-button color="accent" [matMenuTriggerFor]="menu"><mat-icon>add</mat-icon></button>
                }   

              <!-- Else if a node doesn't have a key, it's a mock node to indicate the user must create a position to proceed. -->
              } @else {
                <!-- Show message and add button. -->
                @if (data.config.allowCreate) {
                  <span class="t-10">{{node.name}}</span>
                  <button mat-icon-button color="accent" (click)="createNewPosition(node, 'sibling');"><mat-icon>add</mat-icon></button>
                
                <!-- Show message that they must contact admin. -->
                } @else {
                  <span class="t-10">You aren't allowed to create org parts, please contact your administrator.</span>
                }
              }
              
              @if(node.showLoader) { <mat-spinner class="ml-3" diameter="40" color="accent"></mat-spinner> }
            </div>

            <mat-menu #menu="matMenu">
              <button mat-menu-item color="primary" (click)="createNewPosition(node, 'sibling');">
                <mat-icon color="primary">add</mat-icon>
                <span>Sibling</span>
              </button>
              <button mat-menu-item color="accent" (click)="createNewPosition(node, 'child');">
                <mat-icon color="accent">add</mat-icon>
                <span>Child</span>
              </button>
            </mat-menu>

          </mat-tree-node>

        </mat-radio-group>
        
        } @else {
      
        <!-- Checkbox selection. -->

        <mat-tree-node *matTreeNodeDef="let node" matTreeNodePadding
          [ngStyle]="node.selected ? {'opacity': '100%' } : {'opacity': '30%' }">
          <!-- use a disabled button to provide padding -->
          <button mat-icon-button disabled></button>

          <div class="node-actions">

            <!-- If the node has a key, it's a valid position. -->
            @if (node.key) {
              <mat-checkbox class="m-2" color="primary" [labelPosition]="'after'" (change)="onCheckNode(node, $event)" [checked]="node.node.data.isChecked">{{node.name}} 111 *{{node.node.data.isChecked}}* </mat-checkbox>
              @if (data.config.allowCreate) {
              <button mat-icon-button color="accent" [matMenuTriggerFor]="menu"><mat-icon>add</mat-icon></button>
              }   

            <!-- Else if a node doesn't have a key, it's a mock node to indicate the user must create a position to proceed. -->
            } @else {
              <!-- Show message and add button. -->
              @if (data.config.allowCreate) {
                <span class="t-10">{{node.name}}</span>
                <button mat-icon-button color="accent" (click)="createNewPosition(node, 'sibling');"><mat-icon>add</mat-icon></button>
              
              <!-- Show message that they must contact admin. -->
              } @else {
                <span class="t-10">You aren't allowed to create org parts, please contact your administrator.</span>
              }
            }

            @if(node.showLoader) { <mat-spinner class="ml-3" diameter="40" color="accent"></mat-spinner> }
          </div>

          <mat-menu #menu="matMenu">
            <button mat-menu-item color="primary" (click)="createNewPosition(node, 'sibling');">
              <mat-icon color="primary">add</mat-icon>
              <span>Sibling</span>
            </button>
            <button mat-menu-item color="accent" (click)="createNewPosition(node, 'child');">
              <mat-icon color="accent">add</mat-icon>
              <span>Child</span>
            </button>
          </mat-menu>

        </mat-tree-node>

        <!-- This is the tree node template for expandable nodes -->
        <mat-tree-node *matTreeNodeDef="let node;when: hasChild" matTreeNodePadding
          [ngStyle]="node.selected ? {'opacity': '100%' } : {'opacity': '30%' }">

          <button mat-icon-button matTreeNodeToggle 
            style="align-self: flex-start;"
            [id]="'expand-btn-' + node.expandedKey"
            [attr.aria-label]="'Toggle ' + node.name"
            (click)="onToggleNodeExpand(node, treeControlSecurityGroups.isExpanded(node))">
            <mat-icon class="mat-icon-rtl-mirror">{{treeControlSecurityGroups.isExpanded(node) ? 'expand_more' :
              'chevron_right'}}</mat-icon>
          </button>
          
          <div class="node-actions">
            
            <!-- If the node has a key, it's a valid position. -->
            @if (node.key) {
              <mat-checkbox class="m-2" color="primary" [labelPosition]="'after'" (change)="onCheckNode(node, $event)" [checked]="node.node.data.isChecked">{{node.name}} 222 *{{node.isChecked}}*</mat-checkbox>
              @if (data.config.allowCreate) {
              <button mat-icon-button color="accent" [matMenuTriggerFor]="menu"><mat-icon>add</mat-icon></button>
              }   

            <!-- Else if a node doesn't have a key, it's a mock node to indicate the user must create a position to proceed. -->
            } @else {
              <!-- Show message and add button. -->
              @if (data.config.allowCreate) {
                <span class="t-10">{{node.name}}</span>
                <button mat-icon-button color="accent" (click)="createNewPosition(node, 'sibling');"><mat-icon>add</mat-icon></button>
              
              <!-- Show message that they must contact admin. -->
              } @else {
                <span class="t-10">You aren't allowed to create org parts, please contact your administrator.</span>
              }
            }
            
            @if(node.showLoader) { <mat-spinner class="ml-3" diameter="40" color="accent"></mat-spinner> }
          </div>

          <mat-menu #menu="matMenu">
            <button mat-menu-item color="primary" (click)="createNewPosition(node, 'sibling');">
              <mat-icon color="primary">add</mat-icon>
              <span>Sibling</span>
            </button>
            <button mat-menu-item color="accent" (click)="createNewPosition(node, 'child');">
              <mat-icon color="accent">add</mat-icon>
              <span>Child</span>
            </button>
          </mat-menu>

        </mat-tree-node>
        }

      </mat-tree>

    </div>
  </div>

</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-button mat-dialog-close>Cancel</button>

  @if (data.config.selection === 'single') {
  <button mat-stroked-button color="primary" [disabled]="!selectedPosition" (click)="onSubmit()">Select <mat-icon>check</mat-icon></button>
  } @else {
  <button mat-stroked-button color="primary" [disabled]="!selectedPositions?.length" (click)="onSubmit()">Select <mat-icon>check</mat-icon></button>
  }
</mat-dialog-actions>